import {
    NULL_DIMENSION_BUCKET,
    emptyAnalyticsMetrics,
    type AnalyticsDirectoryUser,
    type AnalyticsMetric,
} from '@bmai/shared';

/**
 * The analytics query contract, re-exported from `@bmai/shared`.
 *
 * These shapes used to be declared here, and the browser compiled them into
 * DuckDB SQL before posting it to telemetry's ad-hoc query endpoint. The query
 * model and the field/metric semantics now live in `@bmai/shared` and telemetry
 * compiles them server-side, so this module is a re-export barrel plus the few
 * genuinely presentational shapes (anomaly display copy and severity) that only
 * the UI needs. Feature code keeps importing from here.
 */

export type {
    AdHocAggregateQuery,
    AdHocAggregateResult,
    AdHocAggregateRow,
    AdHocRecordsQuery,
    AdHocRecordsResult,
    AdHocTrendPoint,
    AdHocTrendQuery,
    AdHocTrendResult,
    AnalyticsDimensionValue,
    AnalyticsField,
    AnalyticsFilter,
    AnalyticsMetric,
    AnalyticsOrderBy,
    AnalyticsSource,
    AnomalyKind,
    AnomalyQuery,
    AnomalyRow,
    AnomalySeverity,
    ConversationTurn,
    InteractionRecord,
    KnownAnalyticsField,
    OlapTable,
    RePromptOverheadQuery,
    RePromptOverheadRow,
    RePromptTurn,
    RePromptTurnsQuery,
    RecordsOrderColumn,
    TimeBucket,
    UserActivityFrequency,
    UserActivityFrequencyQuery,
} from '@bmai/shared';

export { ANALYTICS_METRIC_KEYS, NULL_DIMENSION_BUCKET } from '@bmai/shared';

/**
 * Narrows a group-key value to a real (non-sentinel) string — drops the
 * unclassified bucket before charting, where a `—` slice would read as a
 * category rather than an absence.
 */
export function isResolvedKey(value: string | undefined): value is string {
    return typeof value === 'string' && value !== NULL_DIMENSION_BUCKET;
}

/**
 * The flattened directory identity read from the `directory` dataset. Aliased to
 * the shared `AnalyticsDirectoryUser` so feature code keeps the shorter name;
 * shared has to qualify it because the directory domain owns a different
 * `DirectoryUser`.
 */
export type DirectoryUser = AnalyticsDirectoryUser;

/** One anomaly rendered for display — the label/severity wording the UI derives from an `AnomalyRow`. */
export interface AnomalyEvent {
    label: string;
    detail: string;
    severity: 'high' | 'medium' | 'low';
    /**
     * The identifier to drill into for the underlying turns — a `session_id` for
     * `session_drift`, a `user_id` for `re_prompt`/`declining_activity`. Matches the
     * value of the owning topic's `primaryField`, so it round-trips through the
     * standard evidence route. Absent when the detector could not attribute a subject.
     */
    drillKey?: string;
    /** Human-readable subject for a ranked display (the affected user). */
    subject?: string;
    /** The detector's headline count for this row — topics spanned (drift) or re-prompts (frustration). */
    count?: number;
    /** Tokens attributable to the anomaly itself — the rework burned on the re-prompt restarts (frustration). */
    tokens?: number;
}

export interface AnomalyResult {
    events: AnomalyEvent[];
    total: number;
}

/** Alias kept so feature code reads `emptyMetrics()` as it always has. */
export function emptyMetrics(): Record<AnalyticsMetric, number> {
    return emptyAnalyticsMetrics();
}
